import React, { useState, useEffect, forwardRef, lazy, Suspense } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, CircularProgress, Typography, IconButton, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import { useSnackbar } from '../../contexts/SnackbarContext';
import useWellData from '../hooks/useWellData';

// Lazy load heavy dialog components
const UniversalTransferDialog = lazy(() => import('./UniversalTransferDialog'));
const WellSuccessDialog = lazy(() => import('./WellSuccessDialog'));

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const WellDialog = ({ open, onClose }) => {
    const { t } = useTranslation();
    const { token } = useAuth();
    const { showSnackbar } = useSnackbar();
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [successDialogData, setSuccessDialogData] = useState({
        message: '',
        rewardedItems: [],
        freedWeight: 0
    });
    
    // Use custom hook for data management
    const { loading, error, eligibleItems, filters, throwItems, reload } = useWellData(open, token);
    
    // Open transfer dialog when items are loaded
    useEffect(() => {
        if (eligibleItems && eligibleItems.length > 0 && !error) {
            setTransferDialogOpen(true);
        }
    }, [eligibleItems, error]);
    
    // Handle close and reset state
    const handleClose = () => {
        setTransferDialogOpen(false);
        setSuccessDialogOpen(false);
        onClose();
    };
    
    // Handle throw items confirmation
    const handleThrowConfirm = async (selectedItems) => {
        try {
            const response = await throwItems(selectedItems);
            
            // Close transfer dialog
            setTransferDialogOpen(false);
            
            // Show success dialog with rewards
            setSuccessDialogData({
                message: response.message || t('widgets.well.throwSuccess'),
                rewardedItems: response.rewardedItems || [],
                freedWeight: response.freedWeight || 0
            });
            setSuccessDialogOpen(true);
            
            // Reload data in background for next use
            reload();
        } catch (error) {
            showSnackbar(error.message || t('widgets.well.errorThrowingItems'), 'error');
        }
    };
    
    // Map error messages
    const getErrorMessage = () => {
        if (!error) return '';
        if (error.includes('well location')) {
            return t('widgets.well.notAtWell', 'You must be at a well location');
        }
        if (error.includes('No items')) {
            return t('widgets.well.noEligibleItems', 'No items available to throw into the well');
        }
        return t('widgets.well.errorLoadingItems', error);
    };
    
    // If we have an error, show it in a simple dialog
    if (error) {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">{t('widgets.well.title', 'Well')}</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography color="error">{getErrorMessage()}</Typography>
                </DialogContent>
            </Dialog>
        );
    }
    
    // Loading state
    if (loading) {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }
    
    return (
        <>
            {/* Transfer Dialog for throwing items */}
            {transferDialogOpen && eligibleItems && (
                <Suspense fallback={
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                        <CircularProgress />
                    </Box>
                }>
                    <UniversalTransferDialog
                        open={transferDialogOpen}
                        onClose={() => {
                            setTransferDialogOpen(false);
                            handleClose();
                        }}
                        title={t('widgets.well.dialogTitle', 'Throw items into the well')}
                        sourceItems={eligibleItems}
                        filters={filters}
                        onConfirm={(items, cost) => {
                            handleThrowConfirm(items);
                        }}
                        calculateCost={null} // No cost for throwing into well
                        userGold={0} // Not needed for well
                        warehouseCurrentSize={0} // Not used for well
                        warehouseMaxSize={0} // Not used for well - setting to 0 disables warehouse logic
                        confirmText={t('widgets.well.confirmButton', 'Throw')}
                        costLabel="" // No cost label needed
                    />
                </Suspense>
            )}
            
            {/* Success Dialog */}
            {successDialogOpen && (
                <Suspense fallback={
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                        <CircularProgress />
                    </Box>
                }>
                    <WellSuccessDialog
                        open={successDialogOpen}
                        onClose={handleClose}
                        message={successDialogData.message}
                        rewardedItems={successDialogData.rewardedItems}
                        freedWeight={successDialogData.freedWeight}
                    />
                </Suspense>
            )}
        </>
    );
};

export default WellDialog;